import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  TCallCenter,
  TCreateCallCenterPayload,
  TUpdateCallCenterPayload,
} from "@/models/call-centers.model";
import * as callCentersService from "@/services/call-center/call-centers.service";

type CallCentersState = {
  callCenters: TCallCenter[];
  selectedCallCenter: TCallCenter | null;
  selectedCallCenterLoading: boolean;
  loading: boolean;
  selectionReady: boolean;
};

type CallCentersActions = {
  setSelectedCallCenter: (callCenter: TCallCenter | null) => void;
  setSelectionReady: (ready: boolean) => void;
  fetchCallCenters: (signal?: AbortSignal) => Promise<void>;
  fetchCallCenter: (id: number, signal?: AbortSignal) => Promise<void>;
  createCallCenter: (payload: TCreateCallCenterPayload) => Promise<TCallCenter>;
  updateCallCenter: (payload: TUpdateCallCenterPayload) => Promise<TCallCenter>;
  deleteCallCenter: (id: number) => Promise<void>;
  /** Clears the store back to its initial state — called on logout. */
  reset: () => void;
};

const initialState: CallCentersState = {
  callCenters: [],
  selectedCallCenter: null,
  selectedCallCenterLoading: false,
  loading: false,
  selectionReady: false,
};

export const useCallCentersStore = create<CallCentersState & CallCentersActions>()(
  devtools(
    (set) => ({
      ...initialState,

      setSelectedCallCenter: (callCenter) =>
        set({ selectedCallCenter: callCenter }, false, "setSelectedCallCenter"),

      setSelectionReady: (ready) =>
        set({ selectionReady: ready }, false, "setSelectionReady"),

      fetchCallCenters: async (signal) => {
        set({ loading: true }, false, "fetchCallCenters/pending");
        try {
          const response = await callCentersService.getCallCenters(signal);
          set(
            (state) => {
              const sorted = [...response].sort((a, b) => b.id - a.id);
              // Auto-select when none is selected yet: prefer the first active call
              // center marked as default; fall back to the first active one when
              // none carries the isDefault flag.
              let { selectedCallCenter } = state;
              if (!selectedCallCenter) {
                const active = sorted.filter((cc) => cc.isCallCenterActive);
                const defaultCallCenter = active.find((cc) => cc.isDefault);
                selectedCallCenter = defaultCallCenter ?? active[0] ?? null;
              }
              return { loading: false, callCenters: sorted, selectedCallCenter };
            },
            false,
            "fetchCallCenters/fulfilled"
          );
        } catch {
          // Aborted requests come from a newer fetch replacing this one — leave
          // the loading flag set so the in-flight successor keeps it true.
          if (signal?.aborted) return;
          set({ loading: false }, false, "fetchCallCenters/rejected");
        }
      },

      fetchCallCenter: async (id, signal) => {
        set({ selectedCallCenterLoading: true }, false, "fetchCallCenter/pending");
        try {
          const response = await callCentersService.getCallCenter(id, signal);
          set(
            (state) => {
              const index = state.callCenters.findIndex((cc) => cc.id === response.id);
              const callCenters =
                index === -1
                  ? state.callCenters
                  : state.callCenters.map((cc) => (cc.id === response.id ? response : cc));
              return {
                selectedCallCenterLoading: false,
                selectedCallCenter: response,
                callCenters,
              };
            },
            false,
            "fetchCallCenter/fulfilled"
          );
        } catch {
          // Aborted requests come from the user picking a different call center
          // mid-fetch — leave the loading flag set so the in-flight successor keeps it true.
          if (signal?.aborted) return;
          set({ selectedCallCenterLoading: false }, false, "fetchCallCenter/rejected");
        }
      },

      createCallCenter: async (payload) => {
        const response = await callCentersService.createCallCenter(payload);
        set(
          (state) => ({ callCenters: [response, ...state.callCenters] }),
          false,
          "createCallCenter/fulfilled"
        );
        return response;
      },

      updateCallCenter: async (payload) => {
        const response = await callCentersService.updateCallCenter(payload);
        set(
          (state) => ({
            callCenters: state.callCenters.map((cc) => (cc.id === response.id ? response : cc)),
            selectedCallCenter:
              state.selectedCallCenter?.id === response.id ? response : state.selectedCallCenter,
          }),
          false,
          "updateCallCenter/fulfilled"
        );
        return response;
      },

      deleteCallCenter: async (id) => {
        await callCentersService.deleteCallCenter(id);
        set(
          (state) => ({
            callCenters: state.callCenters.filter((cc) => cc.id !== id),
            selectedCallCenter:
              state.selectedCallCenter?.id === id ? null : state.selectedCallCenter,
          }),
          false,
          "deleteCallCenter/fulfilled"
        );
      },

      reset: () => set(initialState, false, "reset"),
    }),
    { name: "callCenters" }
  )
);
